import React, { useEffect, useState } from 'react';
import { fetchReturns, ReturnResponse, ReturnStatus } from '../../services/returnService';
import { 
  fetchSupplierReturns, 
  SupplierReturnResponse, 
  SupplierReturnStatus,
  SupplierReturnReason 
} from '../../services/supplierReturnService';
import CreateReturnModal from './CreateReturnModal';
import CreateSupplierReturnModal from './CreateSupplierReturnModal';

type TabType = 'customer' | 'supplier';

const CompleteReturnsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('customer');
  const [returns, setReturns] = useState<ReturnResponse[]>([]);
  const [supplierReturns, setSupplierReturns] = useState<SupplierReturnResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  
  useEffect(() => {
    if (activeTab === 'customer') {
      loadReturns();
    } else {
      loadSupplierReturns();
    }
  }, [statusFilter, activeTab]);
  
  const loadReturns = async () => {
    setLoading(true);
    try {
      const result = await fetchReturns({
        status: statusFilter !== 'ALL' ? statusFilter as ReturnStatus : undefined,
        page: 1,
        page_size: 50
      });
      setReturns(result);
    } catch (error) {
      console.error('Failed to load returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierReturns = async () => {
    setLoading(true);
    try {
      const result = await fetchSupplierReturns({
        status: statusFilter !== 'ALL' ? statusFilter as SupplierReturnStatus : undefined,
        page: 1,
        page_size: 50
      });
      setSupplierReturns(result);
    } catch (error) {
      console.error('Failed to load supplier returns:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status: ReturnStatus | SupplierReturnStatus) => {
    const badges = {
      [ReturnStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [ReturnStatus.APPROVED]: 'bg-green-100 text-green-800',
      [ReturnStatus.REJECTED]: 'bg-red-100 text-red-800',
      [SupplierReturnStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [SupplierReturnStatus.SUBMITTED]: 'bg-blue-100 text-blue-800',
      [SupplierReturnStatus.APPROVED]: 'bg-green-100 text-green-800',
      [SupplierReturnStatus.REJECTED]: 'bg-red-100 text-red-800',
      [SupplierReturnStatus.COMPLETED]: 'bg-purple-100 text-purple-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getReasonLabel = (reason: SupplierReturnReason) => {
    const labels = {
      [SupplierReturnReason.DEFECTIVE]: 'Rusak/Cacat',
      [SupplierReturnReason.WRONG_ITEM]: 'Barang Salah',
      [SupplierReturnReason.EXCESS_STOCK]: 'Kelebihan Stok',
      [SupplierReturnReason.EXPIRED]: 'Kadaluarsa',
      [SupplierReturnReason.OTHER]: 'Lainnya',
    };
    return labels[reason] || reason;
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Retur Barang</h1>
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {activeTab === 'customer' ? '+ Retur dari Customer' : '+ Retur ke Supplier'}
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-4 border-b">
            <button
              onClick={() => setActiveTab('customer')}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'customer'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Retur Penjualan (Customer)
            </button>
            <button
              onClick={() => setActiveTab('supplier')}
              className={`pb-2 px-1 font-medium transition-colors ${
                activeTab === 'supplier'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Retur Pembelian (Supplier)
            </button>
          </div>
          
          {/* Filters */}
          <div className="mt-4 flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded"
            >
              <option value="ALL">Semua Status</option>
              {activeTab === 'customer' ? (
                <>
                  <option value={ReturnStatus.PENDING}>Pending</option>
                  <option value={ReturnStatus.APPROVED}>Approved</option>
                  <option value={ReturnStatus.REJECTED}>Rejected</option>
                </>
              ) : (
                <>
                  <option value={SupplierReturnStatus.DRAFT}>Draft</option>
                  <option value={SupplierReturnStatus.SUBMITTED}>Submitted</option>
                  <option value={SupplierReturnStatus.APPROVED}>Approved</option>
                  <option value={SupplierReturnStatus.REJECTED}>Rejected</option>
                  <option value={SupplierReturnStatus.COMPLETED}>Completed</option>
                </>
              )}
            </select>
          </div>
        </div>
        
        {/* Customer Returns List */}
        {activeTab === 'customer' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : returns.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Tidak ada retur penjualan
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. Retur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Refund</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {returns.map((returnItem) => (
                    <tr key={returnItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {returnItem.return_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {returnItem.sale_invoice_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {returnItem.customer_name || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {returnItem.items.map((item, idx) => (
                            <div key={idx}>
                              {item.item_name} ({item.qty} pcs)
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      Rp {returnItem.total_refund.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(returnItem.status)}`}>
                        {returnItem.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(returnItem.created_at).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {returnItem.status === ReturnStatus.PENDING && (
                        <button className="text-blue-600 hover:text-blue-800">
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}

        {/* Supplier Returns List */}
        {activeTab === 'supplier' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : supplierReturns.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Tidak ada retur pembelian ke supplier
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. Retur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alasan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {supplierReturns.map((returnItem) => (
                    <tr key={returnItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {returnItem.return_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {returnItem.supplier_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {returnItem.po_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm">
                          {getReasonLabel(returnItem.reason)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {returnItem.items.map((item, idx) => (
                            <div key={idx}>
                              {item.item_name} ({item.quantity} pcs)
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        Rp {returnItem.total_amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(returnItem.status)}`}>
                          {returnItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(returnItem.created_at).toLocaleDateString('id-ID')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {returnItem.status === SupplierReturnStatus.DRAFT && (
                            <button className="text-blue-600 hover:text-blue-800">
                              Submit
                            </button>
                          )}
                          {returnItem.status === SupplierReturnStatus.SUBMITTED && (
                            <button className="text-green-600 hover:text-green-800">
                              Approve
                            </button>
                          )}
                          <button className="text-gray-600 hover:text-gray-800">
                            Detail
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      
      {/* Create Return Modal - Customer */}
      {showCreate && activeTab === 'customer' && (
        <CreateReturnModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            loadReturns();
          }}
        />
      )}

      {/* Create Supplier Return Modal */}
      {showCreate && activeTab === 'supplier' && (
        <CreateSupplierReturnModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            loadSupplierReturns();
          }}
        />
      )}
    </div>
  );
};

export default CompleteReturnsPage;
